//Library Imports
import React, { useEffect, useState } from "react";
import styled from "styled-components";

//Local Imports
import SentEmailSearch from "../../Campaigns/Emails/Components/SentEmailSearch";
import SelectWithIcon from "modules/Campaigns/Shared/SelectWithIcon";
import ListingTable from "./ListingTable";
import { ReactComponent as Sort } from "../../../assets/img/icons/Sort.svg";
import { addActionModal, getUserWorkflows, resetSelectedPreviewEmailId, resetUserWorkflows } from "../actions";
import store, { RootState } from "store";
import { AUTOMATION_USER_WORKFLOWS } from "constants/types/automationTypes";
import { useSelector } from "react-redux";
import { IUserWorkflowData } from "../interfaces";

interface IAutomationListing {}

const AutomationListing: React.FC<IAutomationListing> = (props: IAutomationListing) => {
  // Props
  const {} = props;

  // Redux States
  const page: number = useSelector((state: RootState) => state.automation.userWorkflows.page);
  const userWorkflowsData: IUserWorkflowData[] = useSelector(
    (state: RootState) => state.automation.userWorkflows.data
  );
  const searchedUserWorkflowsData: IUserWorkflowData[] = useSelector(
    (state: RootState) => state.automation.userWorkflows.searchedData
  );

  // Local States
  const [searchValue, setSearchValue] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<number[]>([]);

  // Local Variables
  let offset =
    page === 0
      ? 0
      : searchValue.trim().length > 0 || filterType.length > 0
      ? searchedUserWorkflowsData.length
      : userWorkflowsData.length;
  const limit = 10;

  // Component Logic
  useEffect(() => {
    getUserWorkflows(offset, limit);
    store.dispatch(addActionModal(false));
    resetSelectedPreviewEmailId();
    return () => {
      resetUserWorkflows(); // Reset user workflows on unmount
    };
  }, []);

  const searchHandler = (searchValue: string) => {
    setSearchValue(searchValue);
    clearSelectedWorkflowIds();
    if (searchValue.trim().length > 0 || filterType.length > 0)
      getUserWorkflows(0, limit, searchValue, filterType);
    else {
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS.SET_SEARCH_TERM,
        payload: searchValue,
      });
    }
  };

  const resetSearchHandler = () => {
    setSearchValue("");
    clearSelectedWorkflowIds();
    getUserWorkflows(0, limit, "", filterType);
    store.dispatch({
      type: AUTOMATION_USER_WORKFLOWS.SET_SEARCH_TERM,
      payload: "",
    });
    store.dispatch({
      type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM,
      payload: { message: "", success: true },
    });
  };

  const filterHandler = (filterType: string) => {
    setFilterType(filterType);
    clearSelectedWorkflowIds();
    if (filterType.length > 0) {
      getUserWorkflows(0, limit, searchValue, filterType);
    } else {
      getUserWorkflows(0, limit, searchValue, filterType);
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS.SET_FILTER_TYPE,
        payload: filterType,
      });
    }
  };

  const getUserWorkflowsNext = () => {
    getUserWorkflows(offset, limit, searchValue, filterType);
  };

  const clearSelectedWorkflowIds = () => {
    setSelectedWorkflowIds([]);
  };

  return (
    <Wrapper>
      <div className="filter-row d-flex justify-content-between">
        <SelectWithIcon
          emoji={<Sort style={{ height: 18, width: 18 }} />}
          defaultValue={{ value: "", label: "Any" }}
          options={[
            { value: "", label: "Any" },
            { value: "draft", label: "Draft" },
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ]}
          heightWidth={15}
          menuWidth={150}
          isCheckbox
          onChange={(value) => filterHandler(value.value)}
          className="workflow-select"
        />
        <div className="search-wrapper">
          <SentEmailSearch
            value={searchValue}
            handleSearch={searchHandler}
            reset={resetSearchHandler}
            placeholder="Search workflows…"
          />
        </div>
      </div>
      <ListingTable
        getUserWorkflowsNext={getUserWorkflowsNext}
        selectedWorkflowIds={selectedWorkflowIds}
        setSelectedWorkflowIds={setSelectedWorkflowIds}
        clearSelectedWorkflowIds={clearSelectedWorkflowIds}
      />
    </Wrapper>
  );
};

export default AutomationListing;

const Wrapper = styled.div`
  background-color: #fff;
  width: calc(100vw - 88px);
  margin-left: -89px;
  margin-right: -90px;
  padding: 24px 88px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    width: calc(100vw - 64px);
    margin-left: -65px;
    margin-right: -65px;
  }
  .label-row {
    color: #67696a;
    font-size: 22px;
    font-weight: 500;
    font-family: "Roboto", sans-serif;
    margin: 20px 0 10px 0;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 18px;
    }
  }
  .workflow-select {
    .qa-control {
      min-width: 150px;
    }
    span[data-id^="option-"] {
      > div {
        margin-top: 1px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 2px;
        }
      }
    }
  }
`;
